import { useMemo, useState } from "react";
import { formatCurrency, toDate } from "../lib/affiliateAnalytics";

const ROWS_PER_PAGE = 10;

export default function StatisticsPage({ analytics, loading }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    from: "",
    to: "",
    linkId: "",
    trader: "",
  });

  const filteredRows = useMemo(() => {
    const rows = analytics?.rows || [];
    const fromTime = filters.from ? new Date(`${filters.from}T00:00:00`).getTime() : null;
    const toTime = filters.to ? new Date(`${filters.to}T23:59:59`).getTime() : null;

    return rows.filter((row) => {
      const createdAt = toDate(row.createdAt)?.getTime() || 0;
      const matchesLink = filters.linkId
        ? String(row.linkId || "").toLowerCase().includes(filters.linkId.toLowerCase())
        : true;
      const matchesTrader = filters.trader
        ? `${row.trader} ${row.traderMeta}`.toLowerCase().includes(filters.trader.toLowerCase())
        : true;
      const matchesFrom = fromTime ? createdAt >= fromTime : true;
      const matchesTo = toTime ? createdAt <= toTime : true;
      return matchesLink && matchesTrader && matchesFrom && matchesTo;
    });
  }, [analytics?.rows, filters]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / ROWS_PER_PAGE));
  const paginatedRows = filteredRows.slice(
    (currentPage - 1) * ROWS_PER_PAGE,
    currentPage * ROWS_PER_PAGE,
  );

  const totals = useMemo(
    () => ({
      turnover: filteredRows.reduce((sum, row) => sum + Number(row.turnover || 0), 0),
      deposits: filteredRows.reduce((sum, row) => sum + Number(row.depositsSum || 0), 0),
      commission: filteredRows.reduce((sum, row) => sum + Number(row.commission || 0), 0),
    }),
    [filteredRows],
  );

  return (
    <div className="space-y-6">
      <section className="rounded-[32px] border border-white/10 bg-[rgba(11,18,31,0.92)] p-6 shadow-[0_30px_80px_rgba(0,0,0,0.28)]">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.28em] text-[var(--aff-muted)]">
              Statistics
            </div>
            <h2 className="mt-2 text-2xl font-black text-white">Referred player performance</h2>
            <p className="mt-2 max-w-2xl text-sm leading-7 text-slate-300">
              Each approved referred deposit now creates real affiliate commission. This table combines referred-user deposits, player turnover, and credited commission in one place.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <QuickStat label="Deposits" value={loading ? "..." : formatCurrency(totals.deposits)} />
            <QuickStat label="Turnover" value={loading ? "..." : formatCurrency(totals.turnover)} />
            <QuickStat label="Commission" value={loading ? "..." : formatCurrency(totals.commission)} />
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-4">
          <FilterInput
            type="date"
            value={filters.from}
            onChange={(value) => {
              setFilters((current) => ({ ...current, from: value }));
              setCurrentPage(1);
            }}
          />
          <FilterInput
            type="date"
            value={filters.to}
            onChange={(value) => {
              setFilters((current) => ({ ...current, to: value }));
              setCurrentPage(1);
            }}
          />
          <FilterInput
            placeholder="Filter by link id"
            value={filters.linkId}
            onChange={(value) => {
              setFilters((current) => ({ ...current, linkId: value }));
              setCurrentPage(1);
            }}
          />
          <FilterInput
            placeholder="Search trader"
            value={filters.trader}
            onChange={(value) => {
              setFilters((current) => ({ ...current, trader: value }));
              setCurrentPage(1);
            }}
          />
        </div>
      </section>

      <section className="overflow-hidden rounded-[32px] border border-white/10 bg-[rgba(11,18,31,0.92)] shadow-[0_30px_80px_rgba(0,0,0,0.28)]">
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-white/10 bg-white/[0.03] text-[11px] uppercase tracking-[0.24em] text-[var(--aff-muted)]">
              <tr>
                <th className="px-5 py-4">Trader</th>
                <th className="px-5 py-4">Link</th>
                <th className="px-5 py-4">Program</th>
                <th className="px-5 py-4">Deposits</th>
                <th className="px-5 py-4">Deposits sum</th>
                <th className="px-5 py-4">Turnover</th>
                <th className="px-5 py-4">Commission</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <PlaceholderRow label="Loading statistics..." />
              ) : paginatedRows.length ? (
                paginatedRows.map((row) => (
                  <tr key={row.id} className="border-b border-white/6 text-slate-200">
                    <td className="px-5 py-4">
                      <div className="font-semibold text-white">{row.trader}</div>
                      <div className="text-xs text-slate-400">{row.traderMeta}</div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="font-medium text-white">{row.linkId}</div>
                      <div className="text-xs text-slate-400">{row.linkComment || "No comment"}</div>
                    </td>
                    <td className="px-5 py-4">
                      <span className="rounded-full border border-white/10 bg-white/[0.05] px-3 py-1 text-xs font-semibold text-white">
                        {row.program === "revenue" ? "Revenue share" : "Turnover share"}
                      </span>
                    </td>
                    <td className="px-5 py-4 font-semibold text-white">{row.deposits}</td>
                    <td className="px-5 py-4">{formatCurrency(row.depositsSum)}</td>
                    <td className="px-5 py-4">{formatCurrency(row.turnover)}</td>
                    <td className="px-5 py-4 text-emerald-300">{formatCurrency(row.commission)}</td>
                  </tr>
                ))
              ) : (
                <PlaceholderRow label="No referred player data matched these filters." />
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-3 border-t border-white/10 px-5 py-4 text-sm text-slate-400 sm:flex-row sm:items-center sm:justify-between">
          <div>
            Page {currentPage} of {totalPages}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              className="rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 text-white disabled:opacity-40"
            >
              Prev
            </button>
            <button
              type="button"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
              className="rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 text-white disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

function QuickStat({ label, value }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3">
      <div className="text-xs uppercase tracking-[0.18em] text-[var(--aff-muted)]">{label}</div>
      <div className="mt-1 text-xl font-black text-white">{value}</div>
    </div>
  );
}

function FilterInput({ type = "text", value, onChange, placeholder = "" }) {
  return (
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
      className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-emerald-400/50 focus:outline-none"
    />
  );
}

function PlaceholderRow({ label }) {
  return (
    <tr>
      <td colSpan={7} className="px-5 py-12 text-center text-sm text-slate-400">
        {label}
      </td>
    </tr>
  );
}
